<template>
  <div class="login">
    <div class="login__halo" aria-hidden="true"></div>

    <div class="login__inner">
      <div class="login__brand">
        <h1 class="login__wordmark">pmm</h1>
        <p class="login__tagline">Digital Ledger — Editorial Financial Intelligence</p>
      </div>

      <div class="al-card login__card">
        <!-- Step 2: TOTP verification -->
        <template v-if="step === 'totp'">
          <div class="login__totp-header">
            <span class="material-symbols-outlined login__totp-icon">lock</span>
            <h2 class="login__totp-title">Two-factor authentication</h2>
            <p class="login__totp-subtitle">Enter the 6-digit code from your authenticator app.</p>
          </div>
          <n-form @submit.prevent="handleTOTP">
            <n-form-item label="Authenticator code">
              <n-input
                v-model:value="totpCode"
                placeholder="000000"
                size="large"
                maxlength="6"
                :allow-input="onlyDigits"
                @keyup.enter="handleTOTP"
              />
            </n-form-item>
            <n-button
              type="primary"
              block
              size="large"
              :loading="authStore.loading"
              @click="handleTOTP"
            >
              Verify
            </n-button>
            <n-button
              text
              block
              size="small"
              style="margin-top: 0.75rem; color: var(--al-on-surface-variant)"
              @click="step = 'credentials'"
            >
              Back to login
            </n-button>
          </n-form>
        </template>

        <!-- Step 1: credentials -->
        <template v-else>
          <n-tabs v-model:value="activeTab" type="segment" size="large">
            <n-tab-pane name="login" tab="Sign in">
              <n-form @submit.prevent="handleLogin">
                <n-form-item label="Email">
                  <n-input
                    v-model:value="loginForm.email"
                    type="email"
                    placeholder="you@example.com"
                    size="large"
                  />
                </n-form-item>
                <n-form-item label="Password">
                  <n-input
                    v-model:value="loginForm.password"
                    type="password"
                    show-password-on="click"
                    placeholder="••••••••"
                    size="large"
                    @keyup.enter="handleLogin"
                  />
                </n-form-item>
                <n-button
                  type="primary"
                  block
                  size="large"
                  :loading="authStore.loading"
                  @click="handleLogin"
                >
                  Enter ledger
                </n-button>
                <div class="login__forgot">
                  <router-link to="/forgot-password" class="login__forgot-link">
                    Forgot password?
                  </router-link>
                </div>
              </n-form>
            </n-tab-pane>

            <n-tab-pane name="register" tab="Create account">
              <n-form @submit.prevent="handleRegister">
                <n-form-item label="Email">
                  <n-input
                    v-model:value="registerForm.email"
                    type="email"
                    placeholder="you@example.com"
                    size="large"
                  />
                </n-form-item>
                <n-form-item label="Password">
                  <n-input
                    v-model:value="registerForm.password"
                    type="password"
                    show-password-on="click"
                    placeholder="Pick a strong password"
                    size="large"
                  />
                </n-form-item>
                <n-form-item label="Confirm password">
                  <n-input
                    v-model:value="registerForm.confirmPassword"
                    type="password"
                    show-password-on="click"
                    placeholder="Repeat your password"
                    size="large"
                  />
                </n-form-item>
                <n-button
                  type="primary"
                  block
                  size="large"
                  :loading="authStore.loading"
                  @click="handleRegister"
                >
                  Open ledger
                </n-button>
              </n-form>
            </n-tab-pane>
          </n-tabs>
        </template>

        <n-alert
          v-if="errorMessage"
          type="error"
          style="margin-top: 1rem"
          closable
          @close="errorMessage = null"
        >
          {{ errorMessage }}
        </n-alert>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { NTabs, NTabPane, NForm, NFormItem, NInput, NButton, NAlert } from 'naive-ui';
import { useAuthStore } from '@/stores/authStore';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const activeTab = ref('login');
const step = ref('credentials');
const errorMessage = ref(null);
const totpCode = ref('');

const loginForm = ref({ email: '', password: '' });
const registerForm = ref({ email: '', password: '', confirmPassword: '' });

const onlyDigits = (val) => /^\d*$/.test(val);

async function handleLogin() {
  errorMessage.value = null;
  try {
    const result = await authStore.login(loginForm.value.email, loginForm.value.password);
    if (result.requires2fa) {
      step.value = 'totp';
    } else {
      router.push(route.query.redirect || '/dashboard');
    }
  } catch (error) {
    errorMessage.value = error.message;
  }
}

async function handleTOTP() {
  errorMessage.value = null;
  try {
    await authStore.verifyTOTP(totpCode.value);
    router.push(route.query.redirect || '/dashboard');
  } catch (error) {
    errorMessage.value = error.message;
    totpCode.value = '';
  }
}

async function handleRegister() {
  errorMessage.value = null;

  if (registerForm.value.password !== registerForm.value.confirmPassword) {
    errorMessage.value = 'Passwords do not match';
    return;
  }

  try {
    await authStore.register(registerForm.value.email, registerForm.value.password);
    router.push('/dashboard');
  } catch (error) {
    errorMessage.value = error.message;
  }
}
</script>

<style scoped>
.login {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  background: var(--al-background);
}

.login__halo {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(circle at 20% 30%, rgba(0, 229, 255, 0.14), transparent 55%),
    radial-gradient(circle at 80% 70%, rgba(195, 245, 255, 0.07), transparent 50%);
  filter: blur(40px);
  pointer-events: none;
}

.login__inner {
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.login__brand {
  text-align: center;
}

.login__wordmark {
  font-family: var(--al-font-headline);
  font-weight: 800;
  font-size: 2.75rem;
  letter-spacing: 0.03em;
  color: var(--al-primary);
  margin: 0;
}

.login__tagline {
  color: var(--al-on-surface-variant);
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.5rem 0 0;
}

.login__card {
  padding: 2rem;
}

.login__forgot {
  text-align: right;
  margin-top: 0.75rem;
}

.login__forgot-link {
  font-size: 0.8125rem;
  color: var(--al-on-surface-variant);
  text-decoration: none;
}

.login__forgot-link:hover {
  color: var(--al-primary);
}

.login__totp-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login__totp-icon {
  font-size: 2.5rem;
  color: var(--al-primary);
}

.login__totp-title {
  font-family: var(--al-font-headline);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0.5rem 0 0.25rem;
  color: var(--al-on-surface);
}

.login__totp-subtitle {
  font-size: 0.875rem;
  color: var(--al-on-surface-variant);
  margin: 0;
}
</style>
